/* eslint-disable react/react-in-jsx-scope */
import { useCallback, useContext, useEffect, useState } from 'react';
import Dropzone from '../components/dropzone';
import { SnackbarProps, defaultSnackbarProps } from './helper';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import JobStatus from '../components/jobStatus';
import useJobStatusData from '../lib/hooks/jobStatusData';
import { UserContext } from '../lib/context/UserContext';
import { useNavigate } from 'react-router-dom';
import TenantContext from '../lib/context/TenantContext';
import useBatchProcessStore from '../store/batchProcessStore';
import { useVendorReview } from '../hooks/useVendorReview';
import VendorReviewModal from '../components/vendorReviewModal';
import type { VendorReviewDecision } from '../types/vendor';
import { useStockReview } from '../hooks/useStockReview';
import StockReviewModal from '../components/stockReviewModal';
import type { StockReviewDecision } from '../types/stock';
import { useCompanyReview } from '../hooks/useCompanyReview';
import CompanyReviewModal from '../components/companyReviewModal';
import type { CompanyReviewDecision } from '../types/companyReview';
import { toast } from 'react-toastify';

const Home = () => {
  const [snackbar, setSnackbar] = useState<SnackbarProps>(defaultSnackbarProps);
  const user = useContext(UserContext);
  const { tenant } = useContext(TenantContext);
  const navigate = useNavigate();

  // Get batchId from the batch process store
  const { batchId } = useBatchProcessStore();

  // Initialize vendor review hook with current batchId and clientId
  const {
    currentRequest,
    submitDecision,
    closeModal,
    paginationInfo: vendorPaginationInfo,
  } = useVendorReview(batchId ?? '', tenant?.id ?? '');

  const {
    currentRequest: currentStockRequest,
    submitDecision: submitStockDecision,
    closeModal: closeStockModal,
    paginationInfo: stockPaginationInfo,
  } = useStockReview(batchId ?? '', tenant?.id ?? '');

  const {
    currentRequest: currentCompanyRequest,
    submitDecision: submitCompanyDecision,
    closeModal: closeCompanyModal,
    paginationInfo: companyPaginationInfo,
  } = useCompanyReview(batchId ?? '', tenant?.id ?? '');

  useEffect(() => {
    if (user?.roles?.includes('SuperAdmin')) {
      navigate('/super-admin/manageTenants');
    }
  }, [user, navigate]);

  // DO NOT DELETE THIS LINE
  useJobStatusData();

  const handleSnackbarClose = () => {};

  /**
   * Handle vendor review decision submission
   * Displays success toast on successful submission, error toast on failure
   */
  const handleVendorReviewSubmit = useCallback(
    async (decision: VendorReviewDecision): Promise<void> => {
      await submitDecision(decision);
      toast.success(
        'Vendor review decision submitted successfully. Processing will continue.',
      );
    },
    [submitDecision],
  );

  /**
   * Handle vendor review modal close
   */
  const handleVendorReviewClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  /**
   * Handle stock review decision submission
   * Displays success toast on successful submission, error toast on failure
   */
  const handleStockReviewSubmit = useCallback(
    async (decision: StockReviewDecision): Promise<void> => {
      await submitStockDecision(decision);
      toast.success(
        'Stock review decision submitted successfully. Processing will continue.',
      );
    },
    [submitStockDecision],
  );

  /**
   * Handle stock review modal close
   */
  const handleStockReviewClose = useCallback(() => {
    closeStockModal();
  }, [closeStockModal]);

  /**
   * Handle company review decision submission
   * Displays success toast on successful submission, error toast on failure
   */
  const handleCompanyReviewSubmit = useCallback(
    async (decision: CompanyReviewDecision): Promise<void> => {
      await submitCompanyDecision(decision);
      toast.success(
        'Company review decision submitted successfully. Processing will continue.',
      );
    },
    [submitCompanyDecision],
  );

  /**
   * Handle company review modal close
   */
  const handleCompanyReviewClose = useCallback(() => {
    closeCompanyModal();
  }, [closeCompanyModal]);

  return (
    <div>
      <Dropzone setSnackbar={setSnackbar} />
      <JobStatus setSnackbar={setSnackbar} />

      <VendorReviewModal
        request={currentRequest}
        onClose={handleVendorReviewClose}
        onSubmit={handleVendorReviewSubmit}
        totalReviews={vendorPaginationInfo.totalItems}
        currentReviewNumber={vendorPaginationInfo.currentPage}
      />

      <StockReviewModal
        request={currentStockRequest}
        onClose={handleStockReviewClose}
        onSubmit={handleStockReviewSubmit}
        totalReviews={stockPaginationInfo.totalItems}
        currentReviewNumber={stockPaginationInfo.currentPage}
      />

      <CompanyReviewModal
        request={currentCompanyRequest}
        onClose={handleCompanyReviewClose}
        onSubmit={handleCompanyReviewSubmit}
        totalReviews={companyPaginationInfo.totalItems}
        currentReviewNumber={companyPaginationInfo.currentPage}
      />

      <Snackbar
        open={snackbar.show}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ show: false, errorMessage: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}>
          {snackbar.errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Home;
